<template>
    <AppShell>
        <!-- Loader -->
        <div v-if="loading" class="flex justify-center items-center py-32">
            <div class="loader"></div>
        </div>

        <!-- Back Button and Breadcrumbs -->
        <div v-if="!loading" class="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
            <button class="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-white transition-colors" @click="goBackToModule">
                <i class="fas fa-arrow-left"></i> Back
            </button>
            <!-- Breadcrumbs -->
            <nav class="text-sm text-ink-muted flex items-center flex-wrap gap-2">
                <button class="hover:text-white transition-colors" @click="navigateTo('module')">Module</button>
                <i class="fas fa-chevron-right text-[10px] text-ink-faint"></i>
                <button class="hover:text-white transition-colors" v-if="module.title" @click="navigateTo('moduleDetails')">
                    {{ module.title || "Loading..." }}
                </button>
                <i v-if="selectedPage.Submodule" class="fas fa-chevron-right text-[10px] text-ink-faint"></i>
                <span v-if="selectedPage.title">{{ selectedPage.Submodule.title }}</span>
                <i v-if="selectedPage.title" class="fas fa-chevron-right text-[10px] text-ink-faint"></i>
                <span v-if="selectedPage.title" class="text-white font-medium">{{ selectedPage.title }}</span>
            </nav>
        </div>

        <!-- Content -->
        <div v-if="!loading" class="flex flex-col lg:flex-row gap-6 items-start">
            <!-- Article View -->
            <div class="card p-6 sm:p-8 w-full lg:w-2/3">
                <h1 class="text-2xl sm:text-3xl font-extrabold text-white mb-5">{{ selectedPage.title || "Select a Page" }}</h1>
                <hr class="border-line mb-8">
                <div v-if="selectedPage.Elements && selectedPage.Elements.length > 0">
                    <!-- Render Elements -->
                    <div v-for="element in selectedPage.Elements" :key="element.id" :id="`question-${element.id}`">
                        <!-- Content Type: Content -->
                        <div v-if="element.type === 'content'" class="prose prose-invert max-w-none prose-headings:text-white prose-a:text-brand
                            hover:prose-a:text-brand-light
                            prose-img:rounded-lg prose-img:shadow-md prose-pre:bg-base-800 prose-pre:border prose-pre:border-line
                            prose-pre:text-slate-50 prose-img:mx-auto prose-img:my-4 prose-code:text-brand-light">
                            <vue-markdown :source="element.contentText" :options="{ html: true }" :plugins="plugins" />
                        </div>

                        <!-- Content Type: Question -->
                        <div v-if="element.type === 'question'" class="flex flex-col gap-4 mb-12">
                            <!-- Question Label -->
                            <div class="badge bg-success text-black self-start">
                                <i class="fas fa-circle-question"></i> Question
                                <i class="fas fa-check-circle" v-if="element.isCompleted"></i>
                            </div>

                            <!-- Question Text -->
                            <vue-markdown :source="element.question" :options="{ html: true }" :plugins="plugins" class="text-left text-ink-muted prose prose-invert max-w-none prose-headings:text-ink-muted" />

                            <!-- Input for Answer -->
                            <input type="text" v-model="element.userSolution" :disabled="element.isCompleted" :style="element.isCompleted ? { backgroundColor: '#1F2937', color: '#6B7280', cursor: 'not-allowed' } : {}"
                                :class="['input', element.isCompleted ? 'cursor-not-allowed' : '']"
                                :placeholder="element.isCompleted ? '' : element.solution" :id="`input-${element.id}`" @keydown.enter="submitAnswer(element.id, element.userSolution)"/>

                            <!-- Submission Controls -->
                            <div class="flex justify-between items-center">
                                <span class="text-ink-muted text-sm">
                                    <i class="fas fa-trophy mr-1 text-yellow-500"></i> +15 points
                                </span>
                                <button :class="element.isCompleted ? 'btn bg-surface-light text-ink-faint cursor-not-allowed' : 'btn-primary'" @click="submitAnswer(element.id, element.userSolution)" :disabled="element.isCompleted"
                                    :id="`button-${element.id}`">
                                    Submit
                                </button>
                            </div>
                        </div>

                        <!-- Content Type: AI Mentored Lab -->
                        <div v-if="element.type === 'aiMentoredLab'" class="flex flex-col gap-4 mb-12">
                            <!-- AI Mentored Lab Label -->
                            <div class="badge bg-purple-500 text-white self-start">
                                <i class="fas fa-robot"></i> AI Mentored Lab
                                <i class="fas fa-check-circle" v-if="element.isCompleted"></i>
                            </div>

                            <!-- AI Mentored Lab Card -->
                            <div class="rounded-xl border border-line bg-surface-light p-6 hover:bg-surface-lighter hover:border-purple-500/40 transition-all cursor-pointer relative" @click="openAiMentoredLab(element)">
                                <!-- Completion Chip -->
                                <div v-if="element.isCompleted" class="badge absolute top-3 right-3 bg-success text-black">
                                    <i class="fas fa-check-circle"></i> Completed
                                </div>

                                <div class="flex flex-col">
                                    <h3 class="text-xl font-bold text-white mb-2">{{ element.AiMentoredLab?.title || element.title }}</h3>
                                    <div class="text-ink-muted mb-2 prose prose-invert prose-sm max-w-none line-clamp-2">
                                        <vue-markdown
                                            :source="truncateDescription(element.AiMentoredLab?.description || 'Learn and practice with AI guidance')"
                                            :options="{ html: true }"
                                            :plugins="plugins"
                                        />
                                    </div>
                                    <div class="text-ink-faint text-sm mb-3 prose prose-invert prose-sm max-w-none line-clamp-1">
                                        <vue-markdown
                                            :source="truncateDescription(element.AiMentoredLab?.goal || 'Complete the lab with AI assistance', 60)"
                                            :options="{ html: true }"
                                            :plugins="plugins"
                                        />
                                    </div>
                                    <div class="flex items-center justify-between">
                                        <div class="flex items-center gap-4">
                                            <span class="text-sm text-purple-400"><i class="fas fa-brain mr-1"></i> AI Guided</span>
                                            <span class="text-sm text-success"><i class="fas fa-trophy mr-1 text-yellow-500"></i> +30 points</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <div class="text-right">
                                                <div class="text-white font-semibold text-sm">Launch Lab</div>
                                                <div class="text-ink-faint text-xs">Click to start</div>
                                            </div>
                                            <i class="fas fa-arrow-right text-purple-400 text-lg"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Completion Controls -->
                            <div v-if="!element.isCompleted" class="flex justify-end items-center">
                                <button @click="markAiLabAsComplete(element.id)" class="btn bg-purple-500 hover:bg-purple-600 text-white">
                                    Mark as Complete
                                </button>
                            </div>
                        </div>

                        <!-- Content Type: Machine Lab -->
                        <div v-if="element.type === 'machineLab'" class="flex flex-col gap-4 mb-12">
                            <!-- Machine Lab Label -->
                            <div class="badge bg-info text-black self-start">
                                <i class="fas fa-server"></i> Machine Lab
                                <i class="fas fa-check-circle" v-if="element.isCompleted"></i>
                            </div>

                            <!-- Machine Lab Card -->
                            <div class="rounded-xl border border-line bg-surface-light p-6 hover:bg-surface-lighter hover:border-info/40 transition-all cursor-pointer relative" @click="openMachineLab(element)">
                                <!-- Completion Chip -->
                                <div v-if="element.isCompleted" class="badge absolute top-3 right-3 bg-success text-black">
                                    <i class="fas fa-check-circle"></i> Completed
                                </div>

                                <div class="flex flex-col">
                                    <h3 class="text-xl font-bold text-white mb-2">{{ element.MachineLab?.title || element.title }}</h3>
                                    <div class="text-ink-muted mb-4 prose prose-invert prose-sm max-w-none line-clamp-3">
                                        <vue-markdown
                                            :source="truncateDescription(element.MachineLab?.description || 'Practice on real machine environments and exploit vulnerabilities')"
                                            :options="{ html: true }"
                                            :plugins="plugins"
                                        />
                                    </div>
                                    <div class="flex items-center justify-between">
                                        <div class="flex items-center gap-4 flex-wrap">
                                            <span class="text-sm text-info"><i class="fas fa-desktop mr-1"></i> Interactive Lab</span>
                                            <span class="text-sm text-success"><i class="fas fa-trophy mr-1 text-yellow-500"></i> +50 points</span>
                                            <span v-if="element.MachineLab?.isExclusive" class="text-sm text-yellow-400"><i class="fas fa-crown mr-1"></i> Exclusive</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <div class="text-right">
                                                <div class="text-white font-semibold text-sm">Launch Lab</div>
                                                <div class="text-ink-faint text-xs">Click to start</div>
                                            </div>
                                            <i class="fas fa-arrow-right text-info text-lg"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Completion Controls -->
                            <div v-if="!element.isCompleted" class="flex justify-end items-center">
                                <button @click="markMachineLabAsComplete(element.id)" class="btn-primary">
                                    Mark as Complete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="text-ink-muted">
                    No content available. Please select a page from the right pane.
                </div>

                <!-- Navigation Buttons -->
                <div class="flex items-center gap-3 mt-8 pt-6 border-t border-line">
                    <!-- Previous Button -->
                    <button v-if="previousPageId" class="btn-secondary" @click="goToPage(previousPageId)">
                        <i class="fas fa-chevron-left text-xs"></i> Previous
                    </button>

                    <!-- Next Button -->
                    <button v-if="nextPageId" class="btn-secondary" @click="goToNextPage">
                        Next <i class="fas fa-chevron-right text-xs"></i>
                    </button>

                    <!-- Complete Module Button -->
                    <button v-else class="btn bg-success text-black hover:opacity-90" @click="completeModule">
                        Complete Module <i class="fas fa-check"></i>
                    </button>
                </div>
            </div>

            <!-- Navigation Pane -->
            <div class="card p-5 w-full lg:w-1/3 self-start lg:sticky lg:top-4">
                <h2 class="text-lg font-bold text-white mb-4">Course Content</h2>
                <ul class="space-y-2">
                    <li v-for="submodule in module.Submodules" :key="submodule.id" class="rounded-xl border border-line bg-surface-light overflow-hidden">
                        <div class="flex items-center gap-2 p-4 cursor-pointer hover:bg-surface-lighter transition-colors" @click="toggleDropdown(submodule.id)">
                            <h3 class="font-semibold text-ink flex items-center gap-2 flex-1 text-left">
                                {{ submodule.title }}
                                <span v-if="submodule.isExclusive" class="badge bg-yellow-500 text-black"><i class="fas fa-crown"></i> Exclusive</span>
                                <i v-if="submodule.isCompleted" class="fas fa-check-circle text-success"></i>
                            </h3>
                            <i :class="[
                                'fas text-ink-faint transition-transform',
                                { 'fa-chevron-down': activeDropdown !== submodule.id, 'fa-chevron-up': activeDropdown === submodule.id }
                            ]"></i>
                        </div>
                        <div v-if="activeDropdown === submodule.id" class="border-t border-line p-2 space-y-1">
                            <div v-for="page in submodule.pages" :key="page.id" class="flex items-center justify-between gap-2 p-2.5 rounded-lg hover:bg-surface transition-colors"
                                :class="String(pageId) === String(page.id) ? 'bg-surface' : ''">
                                <a :href="`/modules/pages/${page.id}`" class="flex items-center gap-2.5 w-full">
                                    <i class="fas fa-book text-xs" :class="String(pageId) === String(page.id) ? 'text-brand' : 'text-ink-faint'"></i>
                                    <p class="text-sm text-left" :class="String(pageId) === String(page.id) ? 'text-brand font-semibold' : 'text-ink-muted'">
                                        {{ page.title }}
                                    </p>
                                </a>
                                <i v-if="page.isCompleted" class="fas fa-check-circle text-success"></i>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </AppShell>
</template>


<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import apiClient from "@/axiosConfig";
import toastr from "toastr";
import markdownItLatex from "markdown-it-latex";
import markdownItHighlight from "markdown-it-highlightjs";
import VueMarkdown from 'vue-markdown-render'
import 'highlight.js/styles/github-dark.css';
import 'markdown-it-latex/dist/index.css';

export default {
    name: "ContentModule",
    components: { AppShell, VueMarkdown },
    data() {
        return {
            activeDropdown: null,
            module: {},
            selectedPage: {},
            previousPageId: null,
            nextPageId: null,
            loading: true,
            plugins: [markdownItLatex, markdownItHighlight]
        };
    },
    computed: {
        pageId() {
            return this.$route.params.id;
        },
    },
    watch: {
        "$route.params.id": {
            immediate: true,
            handler(newPageId) {
                this.fetchPageData(newPageId);
            },
        },
    },
    methods: {
        async fetchPageData(pageId) {
            try {
                const response = await apiClient.get(`/pages/${pageId}`);
                this.selectedPage = response.data;

                await this.fetchModuleDetails(response.data.Submodule.Module.id);
                this.activeDropdown = response.data.submoduleId;

                await this.fetchSubmodulePages(response.data.submoduleId);
                this.calculateNavigation(response.data.id, response.data.submoduleId);

                response.data.Elements.forEach((element) => {
                    if (element.isCompleted && element.type == "question") {
                        element.userSolution = element.solution;
                    }
                });
            } catch (error) {
                if (error.response?.status === 403) {
                    toastr.error(error.response.data.message, "Access Denied");
                    this.$router.push(`/modules/${error.response.data.moduleId}`);
                } else {
                    toastr.error("An error occurred while fetching the page data.", "Error");
                }
                this.loading = false;
            }
        },
        async fetchModuleDetails(moduleId) {
            const response = await apiClient.get(`/modules/${moduleId}`);
            const baseUrl = process.env.VUE_APP_BASE_URL;

            this.module = {
                ...response.data,
                imageUrl: `${baseUrl}/static/modules/banners/${response.data.id}`,
                Submodules: response.data.Submodules.map((submodule) => ({ ...submodule, pages: [] })),
            };
        },
        async fetchSubmodulePages(submoduleId) {
            const response = await apiClient.get(`/submodules/${submoduleId}`);
            const submodule = this.module.Submodules.find((sm) => sm.id === submoduleId);
            if (submodule) submodule.pages = response.data.Pages || [];
        },
        async submitAnswer(elementId, solution) {
            try {
                await apiClient.post(`/progress/question/${elementId}`, { solution });

                const response = await apiClient.get(`/elements/${elementId}`);
                const updatedElement = response.data;

                const elementIndex = this.selectedPage.Elements.findIndex(el => el.id === elementId);
                if (elementIndex !== -1) {
                    // Reactively replace the element so the :class binding flips to the
                    // completed (disabled) style immediately — no imperative DOM styling.
                    this.selectedPage.Elements[elementIndex] = {
                        ...this.selectedPage.Elements[elementIndex],
                        ...updatedElement,
                        isCompleted: true,
                        userSolution: updatedElement.solution ?? solution,
                    };
                }

                toastr.success("Correct answer! You've earned points.");
            } catch (error) {
                console.error(error)
                // Handle any errors and notify the user
                if (error.response && error.response.data && error.response.data.message) {
                    toastr.error(error.response.data.message);
                } else {
                    toastr.error("Error submitting the answer. Please try again.");
                }
            }
        },
        openMachineLab(element) {
            // Navigate to machine lab page using the MachineLab ID
            const labId = element.MachineLab?.id || element.id;
            this.$router.push(`/machine-labs/${labId}`);
            
            // Note: Completion will be tracked when user actually completes the lab
            // The completion endpoint will be called from the machine lab page itself
        },
        openAiMentoredLab(element) {
            // Navigate to AI mentored lab page using the AiMentoredLab ID
            const labId = element.AiMentoredLab?.id || element.id;
            this.$router.push(`/ai-labs/${labId}`);
            
            // Note: Completion will be tracked when user actually completes the lab
            // The completion endpoint will be called from the AI lab page itself
        },
        async markMachineLabAsComplete(elementId) {
            try {
                // Mark the machine lab element as complete
                await apiClient.post(`/progress/machineLab/${elementId}`);
                toastr.success("Machine lab completed! Points awarded.");
                
                // Update the element status locally
                const elementIndex = this.selectedPage.Elements.findIndex(el => el.id === elementId);
                if (elementIndex !== -1) {
                    this.selectedPage.Elements[elementIndex].isCompleted = true;
                }
            } catch (error) {
                console.error("Error marking machine lab as complete:", error);
                if (error.response?.data?.message) {
                    toastr.error(error.response.data.message);
                } else {
                    toastr.error("Error completing machine lab. Please try again.");
                }
            }
        },
        async markAiLabAsComplete(elementId) {
            try {
                // Mark the AI mentored lab element as complete
                await apiClient.post(`/progress/aiMentoredLab/${elementId}`);
                toastr.success("AI mentored lab completed! Points awarded.");
                
                // Update the element status locally
                const elementIndex = this.selectedPage.Elements.findIndex(el => el.id === elementId);
                if (elementIndex !== -1) {
                    this.selectedPage.Elements[elementIndex].isCompleted = true;
                }
            } catch (error) {
                console.error("Error marking AI lab as complete:", error);
                if (error.response?.data?.message) {
                    toastr.error(error.response.data.message);
                } else {
                    toastr.error("Error completing AI lab. Please try again.");
                }
            }
        },
        truncateDescription(text, maxLength = 120) {
            if (!text) return '';
            
            // Remove markdown formatting for length calculation
            const plainText = text.replace(/[*_#`~]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
            
            if (plainText.length <= maxLength) {
                return text;
            }
            
            // Find a good breaking point (end of sentence or word)
            const truncated = plainText.substring(0, maxLength);
            const lastSentence = truncated.lastIndexOf('.');
            const lastSpace = truncated.lastIndexOf(' ');
            
            let breakPoint = maxLength;
            if (lastSentence > maxLength * 0.7) {
                breakPoint = lastSentence + 1;
            } else if (lastSpace > maxLength * 0.8) {
                breakPoint = lastSpace;
            }
            
            return text.substring(0, breakPoint) + (breakPoint < text.length ? '...' : '');
        },
        async completeModule() {
            try {
                // Step 1: Mark the current page as complete if not already completed
                if (!this.selectedPage.isCompleted) {
                    await this.markPageAsComplete(this.selectedPage.id);
                }

                // Step 2: Mark the submodule as complete if it is not already completed
                const submoduleId = this.selectedPage.Submodule.id;
                const submodule = this.module.Submodules.find((sm) => sm.id === submoduleId);

                if (submodule && !submodule.isCompleted) {
                    await this.markSubmoduleAsComplete(submoduleId);
                }

                // Step 3: Call the API to mark the module as complete
                const moduleId = this.module.id;
                const response = await apiClient.post(`/progress/module/${moduleId}`);
                toastr.success(response.data.message || "Module successfully completed!");

                // Step 4: Update module completion status
                this.module.isCompleted = true;

                // Step 5: Redirect to module detail page
                this.$router.push(`/modules/${moduleId}`);
            } catch (error) {
                toastr.error(error.response?.data?.message || "Error marking the module as complete.");
            }
        },
        async calculateNavigation(pageId, submoduleId) {
            try {
                const submodule = this.module.Submodules.find((sm) => sm.id === submoduleId);
                const currentIndex = submodule?.pages.findIndex((p) => p.id === pageId);

                this.previousPageId = currentIndex > 0 ? submodule.pages[currentIndex - 1].id : null;
                this.nextPageId = currentIndex < submodule.pages.length - 1 ? submodule.pages[currentIndex + 1].id : null;

                if (!this.nextPageId) {
                    const nextSubmodule = this.module.Submodules[
                        this.module.Submodules.findIndex((sm) => sm.id === submoduleId) + 1
                    ];
                    if (nextSubmodule) {
                        await this.fetchSubmodulePages(nextSubmodule.id); // Fetch pages of the next submodule
                        if (nextSubmodule.pages.length > 0) {
                            this.nextPageId = nextSubmodule.pages[0].id;
                        }
                    }
                }

                if (!this.previousPageId) {
                    const prevSubmodule = this.module.Submodules[
                        this.module.Submodules.findIndex((sm) => sm.id === submoduleId) - 1
                    ];
                    if (prevSubmodule) {
                        await this.fetchSubmodulePages(prevSubmodule.id); // Fetch pages of the previous submodule
                        if (prevSubmodule.pages.length > 0) {
                            this.previousPageId = prevSubmodule.pages[prevSubmodule.pages.length - 1].id;
                        }
                    }
                }
            } finally {
                this.loading = false; // Stop loading
            }
        },
        async markPageAsComplete(pageId) {
            try {
                await apiClient.post(`/progress/page/${pageId}`);
            } catch (error) {
                // toastr.error(error.response?.data?.message || "Error marking the page as complete.");
            }
        },
        async markSubmoduleAsComplete(submoduleId) {
            try {
                const response = await apiClient.post(`/progress/submodule/${submoduleId}`);
                console.log(response.data.message);

                // Update the submodule status locally
                const submodule = this.module.Submodules.find((sm) => sm.id === submoduleId);
                if (submodule) submodule.isCompleted = true;
            } catch (error) {
                console.error("Error marking submodule as complete:", error);
            }
        },
        async markModuleAsComplete(moduleId) {
            try {
                if (this.module.Submodules.every(submodule => submodule.isCompleted)) {
                    await apiClient.post(`/progress/module/${moduleId}`);
                    this.module.isCompleted = true; // Update the local state
                    console.log(`Module "${this.module.title}" marked as complete!`);
                }
            } catch (error) {
                console.error("Error marking module as complete:", error);
            }
        },
        async goToNextPage() {
            const submoduleId = this.selectedPage.Submodule.id;
            this.loading = true;
            if (!this.selectedPage.isCompleted) {
                await this.markPageAsComplete(this.selectedPage.id);
                await this.markSubmoduleAsComplete(submoduleId);
            }
            const submodule = this.module.Submodules.find((sm) => sm.id === submoduleId);

            const isSubmoduleComplete = submodule.pages.every((page) => page.isCompleted);

            if (isSubmoduleComplete && !submodule.isCompleted) {
                await this.markSubmoduleAsComplete(submoduleId);
            }

            // If it's the last page of the last submodule, show "Complete Module" button
            const isLastPageOfModule =
                this.module.Submodules.every((sm) => sm.isCompleted) &&
                !this.nextPageId;

            if (isLastPageOfModule) {
                return; // This will render the "Complete Module" button
            }

            if (this.nextPageId) {
                this.goToPage(this.nextPageId);
            }
        },
        goToPage(pageId) {
            this.loading = true;
            this.$router.push(`/modules/pages/${pageId}`);
        },
        toggleDropdown(submoduleId) {
            if (this.activeDropdown === submoduleId) {
                this.activeDropdown = null;
            } else {
                this.fetchSubmodulePages(submoduleId);
                this.activeDropdown = submoduleId;
            }
        },
        goBackToModule() {
            this.$router.push(`/modules/${this.module.id}`);
        },
        navigateTo(target) {
            if (target === 'module') {
                this.$router.push(`/modules`);
            } else if (target === 'moduleDetails') {
                this.$router.push(`/modules/${this.module.id}`);
            }
        },
    },
};
</script>

<style scoped>
.loader {
    border: 5px solid rgba(255, 255, 255, 0.08);
    border-top: 5px solid #ff3131;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.text-justify {
    text-align: justify;
}

.fa-chevron-down,
.fa-chevron-up {
    transition: transform 0.2s ease;
}

.prose {
    text-align: justify;
    max-width: 100%;
}

.prose img+em {
    display: block;
    text-align: center;
    font-size: 0.875rem;
    line-height: 1.25rem;
}

.line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
